// Runtime configuration accessor.
//
// Per-environment values are NOT baked into the JS bundle. Instead they are
// injected at runtime by `/config.js` (see apps/web/public/config.js), which
// sets `window.__APP_CONFIG__` and is loaded by index.html BEFORE the app
// bundle. This lets a single built artifact be promoted dev → test → prod
// unchanged (matching the API's image-promotion model) — only the small,
// un-bundled config.js differs per environment's S3 bucket.
//
// `import.meta.env.DEV` is intentionally NOT used here: it answers a different,
// build-time question ("am I the local Vite dev server or a built artifact?"),
// which is orthogonal to the deployment tier.

export type Stage = 'local' | 'dev' | 'test' | 'prod'

/** Environment-gated feature flags. Flip these per environment in config.js. */
export type AppFeatures = Record<string, boolean>

export interface AppConfig {
  /** Deployment tier this artifact is currently serving. */
  stage: Stage
  /** Base URL for the Assessments API (prepended to relative request paths). */
  apiUrl: string
  /** Absolute URL the platform navigation is fetched from. */
  platformNavigationApiUrl: string
  /** Absolute URL the logout POST is sent to. */
  platformLogoutUrl: string
  /** Absolute URL an expired session is sent to; blank keeps the "Session expired" notice. */
  platformLoginUrl: string
  /** Absolute URL the impersonation "leave" exit sends the admin to (platform-owned leave flow). */
  platformImpersonationLeaveUrl: string
  /** Per-environment feature gates. */
  features: AppFeatures
}

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<AppConfig>
  }
}

// Safe fallbacks used when a key is absent from the injected config (and in any
// non-browser context, e.g. SSR/build). Mirrors apps/web/public/config.js.
const DEFAULTS: AppConfig = {
  stage: 'local',
  apiUrl: '',
  platformNavigationApiUrl: '',
  platformLogoutUrl: '',
  platformLoginUrl: '',
  platformImpersonationLeaveUrl: '',
  features: {},
}

/**
 * Read the runtime config live on every call (never cached at module load), so
 * tests can swap `window.__APP_CONFIG__` between cases and the deployed app
 * always reflects whatever config.js the bucket served.
 */
export function getAppConfig(): AppConfig {
  const runtime = (typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined) ?? {}
  return {
    ...DEFAULTS,
    ...runtime,
    features: { ...DEFAULTS.features, ...(runtime.features ?? {}) },
  }
}

/** True only when the named feature gate is explicitly enabled for this environment. */
export function isFeatureEnabled(flag: string): boolean {
  return getAppConfig().features[flag] === true
}
